/**
 * Every hour's figures in one dense table — the precise view beside the
 * chart. Rows inside the container's span are tinted; excluded rows say so
 * (the rule's reason on hover) instead of pretending a rank. Hovering a row
 * only highlights it; a click pins the hour (the card above follows). The
 * card used to follow the hover: its height changed, the table slid under
 * the mouse onto the next row, and the two fed each other — the "lag".
 * For Media Devoted | Created By Ciro Autiero
 */
import { FC } from 'react';

import { cn } from '@/lib/utils';

import { excludeHit } from '../../rules/rule-model';
import { ExcludedMark } from '../../rules/RuleHits';
import { count, hourRange, money, scoreLabel, type HourView } from './hour-chart-math';
import { hasHourTags, HourTagDots } from './tags';

interface Props {
  rows: HourView[];
  showMoney: boolean;
  /** The hour the card shows (pinned, hovered on the chart, or the default). */
  active: number | null;
  pinned: number | null;
  onPin: (hour: number) => void;
  tagColors?: Record<string, string>;
}

const TH = 'sticky top-0 bg-background px-2 py-1 text-left text-[10px] font-semibold uppercase tracking-wide text-muted-foreground';
const TD = 'px-2 py-0.5 font-mono text-xs tabular-nums';

export const HourNumbersTable: FC<Props> = ({ rows, showMoney, active, pinned, onPin, tagColors }) => (
  <div className="max-h-[260px] overflow-y-auto rounded-lg border border-border/60">
    <table className="w-full">
      <thead>
        <tr>
          <th className={TH}>Hour</th>
          <th className={TH}>Rank</th>
          <th className={`${TH} text-right`}>Score</th>
          <th className={`${TH} text-right`}>Unique visits</th>
          <th className={`${TH} text-right`}>Clicks</th>
          <th className={`${TH} text-right`}>Conv.</th>
          {showMoney ? <th className={`${TH} text-right`}>Revenue</th> : null}
          {hasHourTags(rows) ? <th className={TH}>Tags</th> : null}
          <th className={TH}>Here</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((row) => (
          <tr
            key={row.hour}
            onClick={() => onPin(row.hour)}
            aria-selected={pinned === row.hour}
            title={pinned === row.hour ? 'Pinned — click again to release' : 'Click to pin this hour on the card'}
            className={cn('cursor-pointer border-t border-border/30 hover:bg-muted/60', row.lit && 'bg-primary/5', active === row.hour && 'bg-primary/15 hover:bg-primary/20', pinned === row.hour && 'font-semibold')}
          >
            <td className={TD}>{hourRange(row.hour)}</td>
            <td className={cn(TD, row.unranked && 'text-muted-foreground')}>
              <RankCell row={row} />
            </td>
            <td className={`${TD} text-right`}>{scoreLabel(row.score)}</td>
            <td className={`${TD} text-right`}>{count(row.uniqueVisits)}</td>
            <td className={`${TD} text-right`}>{count(row.clicks)}</td>
            <td className={`${TD} text-right`}>{count(row.conversions)}</td>
            {showMoney ? <td className={`${TD} text-right`}>{money(row.revenue)}</td> : null}
            {hasHourTags(rows) ? <td className="px-2 py-0.5"><HourTagDots row={row} colors={tagColors} /></td> : null}
            <td className="px-2 py-0.5 text-[11px] text-muted-foreground">
              {row.inSpan ? (row.slots.length > 0 ? `${row.slots.length} slot${row.slots.length > 1 ? 's' : ''}` : 'in span') : ''}
              {row.extra ? ` ⚡ ${row.extra}` : ''}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

/** "#3", the Flow Optimizer's red excluded mark (a rule took it out), or "— not ranked" (no data at all). */
const RankCell: FC<{ row: HourView }> = ({ row }) => {
  if (!row.unranked) return <>#{row.rank}</>;
  if (excludeHit(row.ruleHits)) return <ExcludedMark label title={row.excludedWhy ?? undefined} />;
  return <span title={row.excludedWhy ?? undefined}>— not ranked</span>;
};
